import React, { useEffect, useMemo } from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createClassroom,
  updateClassroom,
} from "../redux/actions/classroomActions";
import { getBranches } from "../redux/actions/branchActions";
import { getGradeLevels } from "../redux/actions/gradeLevelActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { classroomActions } from "../redux/slices/classroom";
import { cleanFormData } from "../helpers/formHelpers";

import { ClassroomResponseDto } from "../shared/dtos/classroom/ClassroomResponseDto";
import { ClassroomRequestDto } from "../shared/dtos/classroom/ClassroomRequestDto";

const classroomSchema = Yup.object().shape({
  name: Yup.string().required("Classroom name is required"),
  branchId: Yup.string().required("Branch is required"),
  gradeLevelId: Yup.string().required("Grade Level is required"),
  academicYearId: Yup.string().required("Academic Year is required"),
  capacity: Yup.number()
    .required("Capacity is required")
    .positive("Capacity must be positive")
    .integer("Capacity must be an integer"),
  location: Yup.string().required("Location is required"),
});

interface CreateOrUpdateClassroomFormProps {
  classroom?: ClassroomResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateClassroomForm: React.FC<
  CreateOrUpdateClassroomFormProps
> = ({
  classroom = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  // Redux Selectors
  const { items: branches } = useAppSelector((state) => state.branch);
  const { items: gradeLevels } = useAppSelector((state) => state.gradeLevel);
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );
  const { userInfo } = useAppSelector((state) => state.auth);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  // Fetch dependencies on mount if missing
  useEffect(() => {
    if (isSuperAdmin && (!branches || branches.length === 0)) {
      dispatch(getBranches());
    }
    if (!gradeLevels || gradeLevels.length === 0) {
      dispatch(getGradeLevels());
    }
    if (!academicYears || academicYears.length === 0) {
      dispatch(getAcademicYears());
    }
  }, [dispatch, branches, gradeLevels, academicYears, isSuperAdmin]);

  // Options mapped for FormSelectField
  const branchOptions = useMemo(
    () => branches?.map((b) => ({ value: b.id, label: b.name })) || [],
    [branches],
  );
  const gradeOptions = useMemo(
    () => gradeLevels?.map((g) => ({ value: g.id, label: g.name })) || [],
    [gradeLevels],
  );
  const yearOptions = useMemo(
    () => academicYears?.map((y) => ({ value: y.id, label: y.name })) || [],
    [academicYears],
  );

  const initialValues: ClassroomRequestDto = {
    name: classroom?.name || "",
    branchId:
      classroom?.branchId || (isSuperAdmin ? "" : userInfo?.branchId) || "",
    gradeLevelId: classroom?.gradeLevelId || "",
    academicYearId: classroom?.academicYearId || "",
    capacity: classroom?.capacity || 30,
    location: classroom?.location || "",
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(classroomActions.setErrorUpdate(null))
            : () => dispatch(classroomActions.setErrorCreate(null))
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={classroomSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          const cleanedValues: ClassroomRequestDto = {
            name: rawCleaned.name,
            branchId: rawCleaned.branchId,
            gradeLevelId: rawCleaned.gradeLevelId,
            academicYearId: rawCleaned.academicYearId,
            capacity: Number(rawCleaned.capacity),
            location: rawCleaned.location,
          };

          if (isEditing && classroom) {
            dispatch(
              updateClassroom({ id: classroom.id, values: cleanedValues }),
            );
          } else {
            dispatch(createClassroom(cleanedValues));
          }
        }}
        enableReinitialize
      >
        {({ dirty, isValid }: FormikProps<ClassroomRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {isSuperAdmin && (
                  <FormSelectField
                    name="branchId"
                    label="Branch"
                    options={branchOptions}
                  />
                )}
                <FormSelectField
                  name="academicYearId"
                  label="Academic Year"
                  options={yearOptions}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormSelectField
                  name="gradeLevelId"
                  label="Grade Level"
                  options={gradeOptions}
                />
                <FormTextField
                  name="name"
                  label="Class Name (e.g., Grade 5 - A)"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="capacity"
                  label="Capacity"
                  type="number"
                  slotProps={{ htmlInput: { min: 1 } }}
                />
                <FormTextField
                  name="location"
                  label="Location"
                  placeholder="e.g., Block A, Room 101"
                />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Classroom"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateClassroomForm;
